// src/components/alertesCritiques/PanneauLateralAlertes.jsx
// Le panneau LATÉRAL des alertes critiques (GO de Daniel du 16/10/2026),
// ouvert et fermé par le bouton « Alertes » de la barre du haut
// (BoutonAlertesCritiques). L'agent y traite les alertes à son rythme : dès
// 1 280 px, la page à gauche, les alertes à droite ; en dessous, par-dessus
// la page (« Comment ça marche » le dit selon l'écran).
//
// Ce n'est PAS une fenêtre : un <aside role="complementary">, jamais
// role=dialog ni la classe inset-0, jamais une Dialog Radix (même
// modal={false}) ; sinon l'ordonnanceur (ecranOccupe) et les gardes des
// modules le prendraient pour une fenêtre ouverte. Il passe sous toutes les
// fenêtres des modules (z-40) et sous « Session expirée ». Échap ne le ferme
// pas : on le ferme par la croix ou par le bouton de la barre.
//
// Placement : fixed à droite, sous la barre du haut (sa hauteur réelle est
// mesurée, ResizeObserver : avec un slogan elle passe 80 px), jusqu'en bas ;
// 320 px de large, pleine largeur sous 768 px. Dès 1 280 px, la coquille
// pousse la page (marge droite) ; en dessous, le panneau se pose par-dessus,
// sans voile, et « Corriger » le replie (l'ordonnanceur s'en charge). La
// classe right-scroll-bar-position le garde calé à droite quand une fenêtre
// Radix retire l'ascenseur du body.
//
// Deux tailles (GO de Daniel du 16/10/2026) : STANDARD 320 px, GRANDE 520 px,
// par le bouton Agrandir / Réduire de l'en-tête (caché sous 768 px). Le choix
// est retenu par utilisateur (localStorage) et relu à chaque ouverture ; la
// grande taille ne s'applique qu'à partir de 1 600 px (CSS, min-[1600px]).
// En dessous, le choix est gardé, sans effet : un clic sur Agrandir le dit
// dans un encadré au lieu d'agrandir. La coquille suit le CHOIX
// (onTailleChange) : sa marge droite prend la même règle CSS, et elle replie
// le menu de gauche en grande taille sous 1 920 px.
//
// Un groupe dont toutes les lignes sont traitées aujourd'hui (groupe.traite,
// composerPanneau) perd son ton de gravité : en-tête gris neutre, sans
// « CRITIQUE ».
//
// Tout vient du contexte (useAlertesCritiquesContexte) : ce composant ne lit
// ni n'écrit rien lui-même, sauf des états LOCAUX :
//   - la pause (usePanneauEnPause) : une fenêtre est ouverte par-dessus la
//     page, le panneau attend sa fermeture pour se relire. Jamais dans la
//     coquille, qui se rendrait à nouveau chaque seconde ;
//   - « Comment ça marche », déplié d'office la première fois pour
//     l'utilisateur (aideLateraleVue / marquerAideLateraleVue), replié ensuite ;
//   - la taille choisie (tailleLateraleMemorisee / memoriserTailleLaterale)
//     et l'encadré « écran trop petit », qui part au clic suivant, à la
//     fermeture ou après 8 secondes.
//
// En-tête fixe : titre, bouton de taille, croix, progression du jour
// (« Aujourd'hui - 3 traitées sur 11 », « 8 restent », barre), encadré « écran
// trop petit », encadré de pause. Corps qui défile : aide,
// lignes groupées par détecteur (PiecesAlertes, disposition 'etroite'), lignes
// traitées grisées. Pied fixe : « Lu à 10:42 » et « Actualiser ».

import React, { useEffect, useId, useLayoutEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Info, Loader2, Maximize2, Minimize2, X } from 'lucide-react';
import { Button } from '../ui/button';
import LoadingSpinner from '../LoadingSpinner';
import PorteurNotification from '../ui/PorteurNotification';
import { useNotification } from '../../hooks/useNotification';
import { useAlertesCritiquesContexte } from './AlertesCritiques';
import { cleStockage } from './apiAlertesCritiques';
import {
  ID_PANNEAU_LATERAL,
  aideLateraleVue,
  anneeDeSession,
  heureParis,
  marquerAideLateraleVue,
  useEcranAuMoins,
  usePanneauEnPause
} from './useAlertesCritiques';
import {
  BOUTON_SECONDAIRE,
  GroupeDetecteur,
  LigneAlerte,
  LigneTraitee,
  VerificationsEnEchec,
  pluriel,
  useConstats
} from './PiecesAlertes';

// La barre du haut, tant qu'elle n'a pas été mesurée (py-4 et l'en-tête de l'école).
const HAUT_PAR_DEFAUT = 80;
const BOUTON_ACTUALISER = 'h-9 min-w-[140px] shrink-0 whitespace-nowrap justify-center gap-2 px-4';
const ENCADRE_BLEU = 'rounded-lg border border-[#246990]/20 bg-[#eef7fb]';
// Les boutons de l'en-tête (taille, croix) : même gabarit.
const BOUTON_EN_TETE = 'h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#246990]/40';

// --- Taille du panneau (GO de Daniel du 16/10/2026) ---------------------------------------

export const TAILLE_STANDARD = 'standard';
export const TAILLE_GRANDE = 'grande';
/** Dès cette largeur (px CSS), la grande taille (520 px) s'applique ; en dessous, 320 px. */
export const LARGEUR_GRANDE_TAILLE = 1600;
/** L'encadré « écran trop petit » part seul après ce délai. */
export const DUREE_MESSAGE_TAILLE = 8000;
export const MESSAGE_TAILLE = 'Votre écran n\'a pas la taille requise pour avoir un panneau plus grand.';
// Les largeurs, écrites en entier pour Tailwind : 320 px dès 768 px, 520 px
// dès 1 600 px si la grande taille est choisie (la coquille pose la même
// règle sur sa marge droite).
const LARGEUR_STANDARD = 'md:w-80';
const LARGEUR_GRANDE = 'min-[1600px]:w-[520px]';

const cleTailleLaterale = () => cleStockage('alertesCritiques:tailleLaterale', { parAnnee: false });

/**
 * La taille choisie par l'utilisateur (TAILLE_STANDARD par défaut), toutes
 * années. Le choix, pas la taille affichée : sous 1 600 px, « grande » reste
 * retenue sans effet. Ne lève jamais (stockage interdit : standard).
 */
export function tailleLateraleMemorisee() {
  try {
    return localStorage.getItem(cleTailleLaterale()) === TAILLE_GRANDE ? TAILLE_GRANDE : TAILLE_STANDARD;
  } catch {
    return TAILLE_STANDARD;
  }
}

function memoriserTailleLaterale(taille) {
  try {
    localStorage.setItem(cleTailleLaterale(), taille === TAILLE_GRANDE ? TAILLE_GRANDE : TAILLE_STANDARD);
  } catch {
    // Stockage plein ou interdit : le choix vaut pour cette page seulement.
  }
}

/**
 * Le bas de la barre du haut, suivi en direct (ResizeObserver, à défaut le
 * redimensionnement de la fenêtre) tant que le panneau est ouvert.
 */
function useHautBarre(barreRef, repli, actif) {
  const [haut, setHaut] = useState(repli);
  useLayoutEffect(() => {
    if (!actif) return undefined;
    const barre = barreRef && barreRef.current;
    if (!barre || typeof barre.getBoundingClientRect !== 'function') {
      setHaut((precedent) => (precedent === repli ? precedent : repli));
      return undefined;
    }
    const mesurer = () => {
      const bas = Math.round(barre.getBoundingClientRect().bottom);
      const valeur = Number.isFinite(bas) && bas > 0 ? bas : repli;
      setHaut((precedent) => (precedent === valeur ? precedent : valeur));
    };
    mesurer();
    const observateur = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(mesurer) : null;
    if (observateur) observateur.observe(barre);
    window.addEventListener('resize', mesurer);
    return () => {
      if (observateur) observateur.disconnect();
      window.removeEventListener('resize', mesurer);
    };
  }, [barreRef, repli, actif]);
  return haut;
}

/** Rend le focus au bouton « Alertes » de la barre quand la croix ferme le panneau. */
function rendreLeFocusAuBouton() {
  if (typeof document === 'undefined') return;
  const bouton = document.querySelector(`[aria-controls="${ID_PANNEAU_LATERAL}"]`);
  if (bouton && typeof bouton.focus === 'function') bouton.focus({ preventScroll: true });
}

/** Le nombre du badge d'un groupe : ce qui y reste à traiter ; un groupe d'informations, ses lignes. */
function compteDuGroupe(groupe) {
  const suivi = groupe.lignes.some((ligne) => ligne.statut !== 'information');
  if (!suivi) return groupe.lignes.length;
  return groupe.lignes.filter((ligne) => ligne.statut === 'a_traiter' || ligne.statut === 'en_cours').length;
}

// --- Progression du jour --------------------------------------------------------------

function Progression({ avancement }) {
  const { traitees, restent, total, envoyees } = avancement;
  if (total === null || total === undefined || !(total > 0)) return null;
  const pourcent = Math.max(0, Math.min(100, Math.round((traitees / total) * 100)));
  return (
    <div className="mt-1">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-sm text-gray-600">
          Aujourd'hui - {traitees} {pluriel(traitees, 'traitée', 'traitées')} sur {total}
        </p>
        {restent > 0 && (
          <p className="whitespace-nowrap text-sm font-semibold text-gray-900">
            {restent} {pluriel(restent, 'reste', 'restent')}
          </p>
        )}
      </div>
      <div
        role="progressbar"
        aria-label="Alertes traitées aujourd'hui"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pourcent}
        className="mt-2 h-2.5 overflow-hidden rounded-md bg-gray-100"
      >
        <div className="h-full rounded-md bg-[#246990] transition-[width] duration-300" style={{ width: `${pourcent}%` }} />
      </div>
      {envoyees > 0 && (
        <p className="mt-1.5 text-[11px] text-gray-400">
          dont {envoyees} {pluriel(envoyees, 'envoyée', 'envoyées')} - en attente de validation
        </p>
      )}
    </div>
  );
}

// --- « Comment ça marche » --------------------------------------------------------------

/**
 * @param {boolean} parDessus - sous 1 280 px : le panneau se pose par-dessus
 *   la page au lieu de la pousser (la première phrase le dit)
 */
function CommentCaMarche({ intervalleMinutes, sonActif, parDessus }) {
  const id = useId();
  // Déplié d'office la première fois pour cet utilisateur, replié ensuite.
  const [deplie, setDeplie] = useState(() => !aideLateraleVue());
  useEffect(() => {
    if (!aideLateraleVue()) marquerAideLateraleVue();
  }, []);
  const minutes = Number(intervalleMinutes) >= 1 ? Number(intervalleMinutes) : 30;

  return (
    <div>
      <button
        type="button"
        onClick={() => setDeplie((valeur) => !valeur)}
        aria-expanded={deplie}
        aria-controls={id}
        className="rounded-lg text-xs font-semibold text-[#246990] hover:text-[#1f5a7a] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#246990]/40"
      >
        Comment ça marche
      </button>
      {deplie && (
        <div id={id} className={`mt-2 flex items-start gap-2 p-3 text-xs text-[#1b4f6d] ${ENCADRE_BLEU}`}>
          <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#246990]" aria-hidden="true" />
          <ul className="min-w-0 space-y-1 leading-snug">
            <li>
              {parDessus
                ? 'Ce panneau s\'ouvre par-dessus la page ; Corriger le replie, le bouton Alertes le rouvre.'
                : 'Ce panneau reste ouvert pendant que vous travaillez : la page à gauche, les alertes à droite.'}
            </li>
            {/* Espaces insécables : « 1 600 px » ne se coupe jamais en fin de ligne. */}
            <li>Sur un écran d'au moins 1&nbsp;600&nbsp;px de large, le bouton Agrandir donne un panneau plus grand.</li>
            <li>
              Corriger ouvre la fenêtre de correction du module à la place de la page en cours : enregistrez
              d'abord une saisie commencée.
            </li>
            <li>Pendant qu'une fenêtre est ouverte, le panneau se met en pause ; il se met à jour à sa fermeture.</li>
            <li>
              Réglée : l'anomalie a disparu. Envoyée : une correction attend sa validation. Constatée : un
              validateur l'a laissée telle quelle.
            </li>
            <li>
              Tant que ce panneau est ouvert, le panneau central ne s'affiche pas. Après sa fermeture, il revient
              dans {minutes} {pluriel(minutes, 'minute', 'minutes')} s'il reste des alertes.
            </li>
            {sonActif && <li>Un son signale une nouvelle alerte.</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

// --- Le contenu du panneau ouvert ------------------------------------------------------------

/**
 * Monté seulement quand le panneau est ouvert (et pendant son glissement de
 * sortie, avec les dernières props : rien ne se vide sous les yeux de l'agent).
 * @param {boolean} grande - la taille AFFICHÉE est la grande (choix « grande »
 *   et écran d'au moins 1 600 px)
 * @param {boolean} grandeTaillePossible - l'écran fait au moins 1 600 px
 * @param {Function} onBasculerTaille - change le choix (standard / grande)
 */
function ContenuPanneauLateral({
  contexte, notifier, signalerEchec, grande, grandeTaillePossible, onBasculerTaille
}) {
  const {
    groupes, avancement, donnees, intervalleMinutes, sonActif, grandEcran, lectureEnCours, premiereLecture, luLe,
    erreurLecture, fermerPanneau, relirePanneau, actualiser, corriger, voirListe, retirerLigne, signalerConstatOuvert
  } = contexte;
  const enPause = usePanneauEnPause(true);
  const constats = useConstats({
    onLigneConstatee: retirerLigne,
    onActualiser: actualiser,
    notifier,
    signalerEchec,
    prochaineFois: 'à la prochaine lecture'
  });
  const { enConstat, motifs, envoi, envoiEnCours } = constats;
  const [deplies, setDeplies] = useState({});
  const anneeSession = useMemo(() => anneeDeSession(), []);

  // Un motif déplié retient la relecture minutée (la ligne pourrait partir
  // et le motif tapé serait perdu) ; replié ou panneau fermé : elle reprend.
  useEffect(() => {
    signalerConstatOuvert(Boolean(enConstat));
  }, [enConstat, signalerConstatOuvert]);
  useEffect(() => () => signalerConstatOuvert(false), [signalerConstatOuvert]);

  // La ligne du motif déplié a quitté la liste (relecture) : le motif se replie.
  const lignesOuvertes = useMemo(() => {
    const cles = new Set();
    groupes.forEach((groupe) => groupe.lignes.forEach((ligne) => {
      if (!ligne.traitee) cles.add(ligne.k);
    }));
    return cles;
  }, [groupes]);
  // Pendant l'envoi d'un constat rien ne se replie : on revoit à sa fin.
  const { abandonnerConstat } = constats;
  useEffect(() => {
    if (enConstat && !envoi && !lignesOuvertes.has(enConstat)) abandonnerConstat(enConstat);
  }, [enConstat, envoi, lignesOuvertes, abandonnerConstat]);

  // « Écran trop petit » : sous 1 600 px, Agrandir le dit au lieu d'agrandir.
  // L'encadré part au clic suivant sur le bouton, à la fermeture du panneau
  // (ce composant est démonté) ou après 8 secondes ; aussi dès que l'écran
  // s'élargit assez (il deviendrait faux).
  const [messageTaille, setMessageTaille] = useState(false);
  useEffect(() => {
    if (!messageTaille) return undefined;
    const minuterie = setTimeout(() => setMessageTaille(false), DUREE_MESSAGE_TAILLE);
    return () => clearTimeout(minuterie);
  }, [messageTaille]);
  useEffect(() => {
    if (grandeTaillePossible) setMessageTaille(false);
  }, [grandeTaillePossible]);

  const basculerTaille = () => {
    if (!grandeTaillePossible) {
      setMessageTaille((affiche) => !affiche);
      return;
    }
    setMessageTaille(false);
    onBasculerTaille();
  };

  const fermer = () => {
    fermerPanneau();
    rendreLeFocusAuBouton();
  };

  const corrigerLigne = (item, lien) => {
    if (envoiEnCours.current) return;
    corriger({ detecteurId: item.detecteurId, cle: item.cle, lien });
  };

  const voirListeLigne = (item, lien) => {
    if (envoiEnCours.current) return;
    voirListe({ detecteurId: item.detecteurId, cle: item.cle, lien });
  };

  const aucuneDonnee = !donnees;

  return (
    <>
      <header className="shrink-0 border-b border-gray-200 px-5 pb-3 pt-4">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-lg font-bold text-gray-900">Alertes critiques</h2>
          <div className="flex shrink-0 items-center gap-1">
            {/* Taille du panneau : caché sous 768 px (pleine largeur). */}
            <button
              type="button"
              onClick={basculerTaille}
              aria-label={grande ? 'Réduire le panneau' : 'Agrandir le panneau'}
              aria-pressed={grande}
              className={`hidden md:inline-flex ${BOUTON_EN_TETE}`}
            >
              {grande
                ? <Minimize2 className="h-4 w-4" aria-hidden="true" />
                : <Maximize2 className="h-4 w-4" aria-hidden="true" />}
            </button>
            <button
              type="button"
              onClick={fermer}
              aria-label="Fermer le panneau des alertes"
              className={`inline-flex ${BOUTON_EN_TETE}`}
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>
        <Progression avancement={avancement} />
        {messageTaille && !grandeTaillePossible && (
          <p role="status" className={`mt-3 px-3 py-2 text-xs text-[#1b4f6d] ${ENCADRE_BLEU}`}>
            {MESSAGE_TAILLE}
          </p>
        )}
        {enPause && (
          <p role="status" className={`mt-3 px-3 py-2 text-xs text-[#1b4f6d] ${ENCADRE_BLEU}`}>
            Une fenêtre est ouverte - le panneau se met à jour à sa fermeture.
          </p>
        )}
      </header>

      <div className={`min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4 transition-opacity${enPause ? ' opacity-60' : ''}`}>
        <CommentCaMarche intervalleMinutes={intervalleMinutes} sonActif={sonActif} parDessus={!grandEcran} />

        {premiereLecture && <LoadingSpinner />}

        {aucuneDonnee && !premiereLecture && erreurLecture && (
          <p className="py-6 text-center text-sm text-gray-500">Les alertes n'ont pas pu être lues pour le moment.</p>
        )}

        {avancement.toutTraite && (
          <p className={`p-3 text-sm text-[#1b4f6d] ${ENCADRE_BLEU}`}>Tout est traité pour aujourd'hui.</p>
        )}

        {avancement.aucune && (
          <p className="py-6 text-center text-sm text-gray-500">Aucune alerte à traiter.</p>
        )}

        {groupes.map((groupe) => (
          <GroupeDetecteur
            key={groupe.id}
            detecteur={groupe}
            lignes={groupe.lignes}
            compte={compteDuGroupe(groupe)}
            traite={Boolean(groupe.traite)}
            disposition="etroite"
            deplie={Boolean(deplies[groupe.id])}
            onDeplier={() => setDeplies((precedent) => ({ ...precedent, [groupe.id]: true }))}
          >
            {(item) => (item.traitee ? (
              <LigneTraitee key={item.k} ligne={item.ligne} statut={item.statut} />
            ) : (
              <LigneAlerte
                key={item.k}
                disposition="etroite"
                ligne={item.ligne}
                lienDetecteur={groupe.lien}
                anneeSession={anneeSession}
                enCours={item.statut === 'en_cours'}
                nouvelle={Boolean(item.nouvelle)}
                enConstat={enConstat === item.k}
                motif={motifs[item.k] || ''}
                envoi={envoi}
                onMotif={(texte) => constats.changerMotif(item.k, texte)}
                onCorriger={(lien) => corrigerLigne(item, lien)}
                onVoirListe={(lien) => voirListeLigne(item, lien)}
                onConstater={() => constats.ouvrirConstat(item.k)}
                onConfirmer={() => constats.confirmerConstat(item.detecteurId, item.ligne)}
                onAnnuler={constats.annulerConstat}
              />
            ))}
          </GroupeDetecteur>
        ))}

        <VerificationsEnEchec erreurs={donnees?.erreurs} suite="à la prochaine lecture" />
      </div>

      <footer className="flex shrink-0 items-center justify-between gap-3 border-t border-gray-200 bg-gray-50 px-4 py-3">
        <p className={`min-w-0 text-xs ${erreurLecture ? 'text-amber-700' : 'text-gray-500'}`} aria-live="polite">
          {erreurLecture
            ? 'Lecture impossible - nouvel essai dans 1 min'
            : (luLe ? `Lu à ${heureParis(luLe)}` : '')}
        </p>
        <Button
          type="button"
          className={`${BOUTON_ACTUALISER} ${BOUTON_SECONDAIRE}`}
          onClick={() => { relirePanneau(); }}
          disabled={lectureEnCours}
        >
          {lectureEnCours && <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />}
          Actualiser
        </Button>
      </footer>
    </>
  );
}

// --- Le panneau ---------------------------------------------------------------------------

/**
 * À monter dans les `children` du fournisseur AlertesCritiques (il lit le
 * contexte), une seule fois, dans la coquille de l'administration.
 * @param {React.RefObject<HTMLElement>} [barreRef] - la barre du haut : le
 *   panneau se cale sous elle, à sa hauteur réelle
 * @param {number} [haut] - la hauteur de repli, sans barre mesurable (80 px)
 * @param {Function} [onTailleChange] - (taille: 'standard'|'grande') : le CHOIX
 *   de l'utilisateur, au montage, à chaque ouverture (relu dans le stockage)
 *   et à chaque clic. La coquille en tire sa marge droite et le repli du menu.
 */
export default function PanneauLateralAlertes({ barreRef = null, haut = HAUT_PAR_DEFAUT, onTailleChange = null }) {
  const contexte = useAlertesCritiquesContexte();
  // Hors du panneau ouvert : les messages d'un constat parti juste avant la
  // fermeture s'affichent quand même.
  const { notification, notifier, masquer, signalerEchec } = useNotification();
  const ouvert = Boolean(contexte.actif && contexte.panneauOuvert);
  const hautBarre = useHautBarre(barreRef, haut, ouvert);

  // La taille : le choix retenu, et la taille affichée (grande seulement dès
  // 1 600 px, suivi en direct : la fenêtre s'élargit, il la retrouve).
  const [taille, setTaille] = useState(tailleLateraleMemorisee);
  const grandeTaillePossible = useEcranAuMoins(LARGEUR_GRANDE_TAILLE);
  const grande = taille === TAILLE_GRANDE && grandeTaillePossible;
  // Relu à chaque ouverture : un autre onglet a pu le changer. Avant
  // l'affichage, pour que le panneau n'arrive pas à la mauvaise largeur.
  useLayoutEffect(() => {
    if (ouvert) setTaille(tailleLateraleMemorisee());
  }, [ouvert]);
  useLayoutEffect(() => {
    if (onTailleChange) onTailleChange(taille);
  }, [taille, onTailleChange]);
  const basculerTaille = () => {
    const suivante = taille === TAILLE_GRANDE ? TAILLE_STANDARD : TAILLE_GRANDE;
    setTaille(suivante);
    memoriserTailleLaterale(suivante);
  };

  // initial={false} : rétabli ouvert au chargement (F5), le panneau est déjà
  // là ; il ne glisse qu'à l'ouverture et à la fermeture.
  return (
    <>
      <AnimatePresence initial={false}>
        {ouvert && (
          <motion.aside
            key="panneau-alertes-critiques"
            id={ID_PANNEAU_LATERAL}
            role="complementary"
            aria-label="Alertes critiques"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className={`right-scroll-bar-position fixed bottom-0 right-0 z-40 flex w-full flex-col border-l border-gray-200 bg-white shadow-xl transition-[width] duration-300 ease-out ${LARGEUR_STANDARD}${
              taille === TAILLE_GRANDE ? ` ${LARGEUR_GRANDE}` : ''
            }`}
            style={{ top: hautBarre }}
          >
            <ContenuPanneauLateral
              contexte={contexte}
              notifier={notifier}
              signalerEchec={signalerEchec}
              grande={grande}
              grandeTaillePossible={grandeTaillePossible}
              onBasculerTaille={basculerTaille}
            />
          </motion.aside>
        )}
      </AnimatePresence>

      <PorteurNotification notification={notification} onFermer={masquer} />
    </>
  );
}
